import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from '../contexts/ToastContext';

interface UseAutoLogoutOptions {
  timeoutMinutes?: number;
  showWarning?: boolean;
  warningMinutes?: number;
}

export function useAutoLogout({
  timeoutMinutes = 60,
  showWarning = true,
  warningMinutes = 5
}: UseAutoLogoutOptions = {}) {
  const { user, signOut } = useAuth();
  const { showWarning: showToastWarning, showError } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Reset the timer on user activity
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }

    // Set new timeouts
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = (timeoutMinutes - warningMinutes) * 60 * 1000;

    // Set warning timeout
    if (showWarning) {
      warningTimeoutRef.current = setTimeout(() => {
        showToastWarning(
          `You will be automatically logged out in ${warningMinutes} minutes due to inactivity.`,
          { duration: 10000 }
        );
      }, warningMs);
    }

    // Set logout timeout
    timeoutRef.current = setTimeout(async () => {
      try {
        await signOut();
        // Navigate to thank you page (use window.location for auto-logout since we might not have router context)
        if (window.location.pathname !== '/thank-you') {
          window.location.href = '/thank-you';
        }
      } catch (error) {
        console.error('Auto logout error:', error);
      }
    }, timeoutMs);
  }, [timeoutMinutes, warningMinutes, showWarning, signOut, showToastWarning, showError]);

  // Activity event handlers
  const handleActivity = useCallback(() => {
    if (user) {
      resetTimer();
    }
  }, [user, resetTimer]);

  useEffect(() => {
    if (!user) {
      // Clear timeouts when user is not logged in
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      return;
    }

    // Set up activity listeners
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start the timer
    resetTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [user, handleActivity, resetTimer]);

  // Return the current timeout status
  const getTimeRemaining = useCallback(() => {
    if (!user || !timeoutRef.current) return null;
    
    const elapsed = Date.now() - lastActivityRef.current;
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const remaining = Math.max(0, timeoutMs - elapsed);
    
    return {
      minutes: Math.floor(remaining / (60 * 1000)),
      seconds: Math.floor((remaining % (60 * 1000)) / 1000),
      totalMs: remaining
    };
  }, [user, timeoutMinutes]);

  return {
    getTimeRemaining,
    resetTimer
  };
}
